import { type FC } from "react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Separator } from "@/shared/components/ui/separator";
import { cn } from "@/shared/utils/core";
import { formatCurrency } from "@/shared/utils/formatters";

import type { EstateItem } from "@/shared/types/estateTypes";

const OWNERSHIP_OPTIONS = [
  { value: "sole", label: (name: string) => `By ${name} alone` },
  { value: "with-someone", label: () => "With someone else" },
  { value: "in-trust", label: () => "In a trust" },
  { value: "not-sure", label: () => "Not sure" },
] as const;

interface LegalAuthorityPropertyCardProps {
  property: EstateItem;
  personFirstName: string;
  selectedOwnership: string | null;
  hideOwnership?: boolean;
  hideActions?: boolean;
  showOwnershipError?: boolean;
  onOwnershipChange: (value: string) => void;
  onEdit: () => void;
  onRemove: () => void;
}

export const LegalAuthorityPropertyCard: FC<LegalAuthorityPropertyCardProps> = ({
  property,
  personFirstName,
  selectedOwnership,
  hideOwnership,
  hideActions,
  showOwnershipError,
  onOwnershipChange,
  onEdit,
  onRemove,
}) => {
  const meta = property.metadata as Record<string, string> | undefined;
  const addressLine1 = meta?.street_address || property.item_name;
  const addressLine2 =
    meta?.city && meta?.state ? `${meta.city}, ${meta.state} ${meta.zip_code ?? ""}`.trim() : null;

  return (
    <div className="flex flex-col gap-6 rounded-lg border border-primary bg-white p-6">
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-base font-medium">{addressLine1}</p>
          {addressLine2 && <p className="text-sm text-secondary">{addressLine2}</p>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <p className="text-base font-semibold">{formatCurrency(property.balance)}</p>
          <p className="text-2xs text-tertiary">estimate</p>
        </div>
      </div>

      {!hideOwnership && (
        <>
          <Separator />

          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium">How was this property owned?</p>

            <div className="flex flex-wrap items-center gap-3">
              {OWNERSHIP_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => onOwnershipChange(value)}
                  className={cn(
                    "rounded-full border px-3 py-2 text-sm",
                    selectedOwnership === value
                      ? "border-brand bg-brand-subtle font-medium text-brand"
                      : "border-primary bg-white text-primary",
                  )}
                >
                  {label(personFirstName)}
                </button>
              ))}
            </div>
            <FieldError
              message={showOwnershipError ? "Select how this property was owned" : undefined}
            />
          </div>
        </>
      )}

      {!hideActions && (
        <div className="flex items-center gap-4">
          <Button variant="link" onClick={onEdit}>
            Edit details
          </Button>
          <Button variant="link" onClick={onRemove}>
            Remove
          </Button>
        </div>
      )}
    </div>
  );
};
